import { useEffect, useState } from "react";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Clock,
  Plus,
  Minus,
  PoundSterling,
  LoaderCircle,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { Separator } from "@/components/ui/separator.tsx";
import { cn } from "@/lib/utils.ts";
import { Link } from "react-router";
import { useUser } from "@/hooks/useUser.ts";
import { Transaction } from "@/types/transaction.ts";
import { getTransactions } from "@/api/getTransactions.ts";
import { setTitle } from "@/hooks/useHeader.ts";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { useTranslation } from "@/i18n";

const Wallet = () => {
  const user = useUser();
  const [transactions, setTransactions] = useState<Transaction[]>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const { t } = useTranslation();

  useEffect(() => {
    setTitle(t("wallet.title"));
    (async () => {
      try {
        const response = await getTransactions();
        setTransactions(response.data.results);
        setLoading(false);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e: unknown) {
        setError(t("wallet.fetchFailed"));
      }
    })();
  }, [t]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="mx-auto flex h-full max-w-md min-w-[45vw] flex-grow flex-col">
      <div className="flex h-full flex-col gap-4">
        <Card className="gap-0 border-none shadow-lg">
          <CardHeader className="p-4">
            <CardDescription>{t("wallet.currentBalance")}</CardDescription>
            <CardTitle className="flex items-center text-3xl font-bold">
              <PoundSterling className="text-primary mr-1 h-6 w-6" />
              {user!.balance}
            </CardTitle>
          </CardHeader>
          <CardFooter className="flex gap-2 p-4 pt-2 [&>*]:flex-1">
            <Link to="/wallet/deposit">
              <Button className="w-full" variant="default">
                <Plus className="mr-2 h-4 w-4" />
                {t("wallet.deposit")}
              </Button>
            </Link>
            <Link to="/wallet/withdraw">
              <Button className="w-full" variant="outline">
                <Minus className="mr-2 h-4 w-4" />
                {t("wallet.withdraw")}
              </Button>
            </Link>
          </CardFooter>
        </Card>

        <Card className="flex-grow">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle>{t("wallet.transactionHistory")}</CardTitle>
              <Clock className="text-muted-foreground h-4 w-4" />
            </div>
          </CardHeader>
          <CardContent className="flex h-full flex-col p-0">
            <ScrollArea className="h-0 flex-grow">
              {!!error && (
                <Alert variant="destructive" className="mx-auto my-4 w-fit">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>{t("common.somethingWentWrong")}</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {loading ? (
                <LoaderCircle className="mx-auto animate-spin" />
              ) : transactions?.length ? (
                <div className="space-y-0">
                  {transactions.map((transaction) => (
                    <div key={transaction.id}>
                      <div className="flex items-center justify-between p-4">
                        <div className="flex items-center space-x-3">
                          <div
                            className={cn(
                              "rounded-full p-2",
                              +transaction.amount > 0
                                ? "bg-green-100"
                                : "bg-red-100",
                            )}
                          >
                            {+transaction.amount > 0 ? (
                              <ArrowDownCircle className="h-5 w-5 text-green-600" />
                            ) : (
                              <ArrowUpCircle className="h-5 w-5 text-red-600" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium">
                              {transaction.narrative}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {formatDate(transaction.created_at)}
                            </p>
                          </div>
                        </div>
                        <div
                          className={cn(
                            "flex-shrink-0 text-sm font-medium",
                            +transaction.amount > 0
                              ? "text-green-600"
                              : "text-red-600",
                          )}
                        >
                          {t("wallet.transactionAmount", {
                            sign: +transaction.amount > 0 ? "+" : "-",
                            amount: transaction.amount.replace("-", ""),
                          })}
                        </div>
                      </div>
                      <Separator />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-muted-foreground p-4 text-center">
                  {t("wallet.noTransactions")}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Wallet;
